import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchPortNames } from '../data-provider/port-names';
import type { PortName, PortNameFilters, PortNames } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const portNamesQueryOptions = (
  filters: Partial<PortNameFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const portNames = await fetchPortNames(filters, { signal });
      put(portNames.list);
      return portNames;
    },
    queryKey: CACHE_KEYS.portNames(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_PORT_NAMES_CACHE_KEY = btoa('internal-port-names-cache-key');

const INTERNAL_QUERY_PORT_NAMES_KEY = portNamesQueryOptions({
  name: INTERNAL_PORT_NAMES_CACHE_KEY,
}).queryKey;

const getPortNameFallback = (portNameId: string): PortName => {
  logger.warn(`Port "${portNameId}" was not found. Verify the data model.`);

  return {
    code: portNameId,
    id: portNameId,
    label: portNameId,
    name: portNameId,
  };
};

export const put = (portNames: PortName[]) => {
  const data = queryClient.getQueryData<PortNames>(INTERNAL_QUERY_PORT_NAMES_KEY);

  const newPortNames = portNames.reduce(
    (acc, portName) => {
      acc[String(portName.id)] = portName;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_PORT_NAMES_KEY, {
    list: Object.values(newPortNames),
    map: newPortNames,
  });
};

export const getAll = (): PortNames | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_PORT_NAMES_KEY);
};

export const find = async (portNameId: string): Promise<PortName> => {
  const data = queryClient.getQueryData<PortNames>(INTERNAL_QUERY_PORT_NAMES_KEY);

  const portName = data?.map[portNameId];
  if (portName) {
    return portName;
  }

  try {
    const newData = await queryClient.query(portNamesQueryOptions({ search: { id: portNameId } }));
    if (newData.map[portNameId]) {
      put(newData.list);
      return newData.map[portNameId];
    }
  } catch {
    return getPortNameFallback(portNameId);
  }

  return getPortNameFallback(portNameId);
};

export const findAll = async (portNameIds: string[]): Promise<PortName[]> => {
  const data = queryClient.getQueryData<PortNames>(INTERNAL_QUERY_PORT_NAMES_KEY);

  const missingIds: string[] = [];
  const portNames: PortName[] = [];

  for (const portNameId of portNameIds) {
    const portName = data?.map[portNameId];
    if (portName) {
      portNames.push(portName);
    } else {
      missingIds.push(portNameId);
    }
  }

  if (!missingIds.length) {
    return portNames;
  }

  try {
    const newData = await queryClient.query(portNamesQueryOptions({ search: { id: missingIds.join(',') } }));
    put(newData.list);
    for (const missingId of missingIds) {
      const portName = newData.map[missingId];
      if (portName) {
        portNames.push(portName);
      } else {
        portNames.push(getPortNameFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      portNames.push(getPortNameFallback(missingId));
    }
  }

  return portNames;
};
